import type { ComponentProps, ReactNode } from "react";
import { Link } from "@tanstack/react-router";

type LinkTo = ComponentProps<typeof Link>["to"];

type TagPillVariant = "link" | "filter" | "chip";
type TagPillSize = "sm" | "md";

interface TagPillProps {
  /**
   * - `link`: plain anchor text (mega-menu)
   * - `filter`: rounded-full pill with category accent (hero related, /browse)
   * - `chip`: static rounded-lg badge (JobCard Remote/Location/Salary)
   */
  variant: TagPillVariant;
  /** Padding size. `filter` and `chip` honour this; `link` ignores. */
  size?: TagPillSize;
  /** Tailwind bg (+ optional text color) class. Defaults to bg-gray-200 for filter. */
  bgColor?: string | null;
  leadingIcon?: ReactNode;
  trailingCount?: number | null;
  className?: string;
  children: ReactNode;
  /** Presence of `to` switches the root element from `<span>` to `<Link>`. */
  to?: LinkTo;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}

const VARIANT_CLASSES: Record<TagPillVariant, string> = {
  link: "text-gray-700 transition-colors hover:text-gray-900",
  filter:
    "inline-flex items-center rounded-full transition-all duration-150 hover:shadow-sm hover:brightness-95",
  chip: "inline-flex items-center gap-1 rounded-lg font-medium",
};

const FILTER_SIZE: Record<TagPillSize, string> = {
  sm: "px-4 py-1.5 text-sm",
  md: "px-4 py-2 text-sm font-medium",
};

const CHIP_SIZE: Record<TagPillSize, string> = {
  sm: "px-3 py-1 text-sm",
  md: "px-3 py-1.5 text-sm",
};

export default function TagPill({
  variant,
  size = "sm",
  bgColor,
  leadingIcon,
  trailingCount,
  className,
  children,
  to,
  params,
  search,
}: TagPillProps) {
  const sizeClasses =
    variant === "filter"
      ? FILTER_SIZE[size]
      : variant === "chip"
        ? CHIP_SIZE[size]
        : "";

  const bgClass =
    variant === "filter" ? (bgColor ?? "bg-gray-200") : (bgColor ?? "");

  const classes = [VARIANT_CLASSES[variant], sizeClasses, bgClass, className]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      {leadingIcon}
      {children}
      {trailingCount != null && (
        <span className="ml-2 text-gray-400 tabular-nums">{trailingCount}</span>
      )}
    </>
  );

  if (to != null) {
    const linkProps = { to, params, search } as ComponentProps<typeof Link>;
    return (
      <Link {...linkProps} className={classes}>
        {body}
      </Link>
    );
  }

  return <span className={classes}>{body}</span>;
}
